import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ProductCard({ product, badgeColor = "bg-rani" }) {
  const img = (product.images || [])[0] || "https://images.unsplash.com/photo-1576871337632-b9aef4c17ab9?w=600";
  const wishlist = useWishlist();
  const { user } = useAuth();
  const wished = wishlist?.isInWishlist(product.id);

  const onHeart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("Sign in to use wishlist"); return; }
    try {
      await wishlist.toggle(product.id);
      toast.success(wished ? "Removed from wishlist" : "Added to wishlist");
    } catch (e) {
      toast.error("Could not update wishlist");
    }
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="group relative block bg-white rounded-2xl md:rounded-3xl border border-deepbrown/10 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
      data-testid={`product-card-${product.id}`}
    >
      <div className="aspect-square overflow-hidden bg-muted relative">
        <img
          src={img}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {product.featured && (
          <span className={`absolute top-3 left-3 ${badgeColor} text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full`}>
            Featured
          </span>
        )}
        <button
          onClick={onHeart}
          className={`absolute top-3 right-3 h-9 w-9 rounded-full grid place-items-center transition-all ${
            wished ? "bg-rani text-white opacity-100" : "bg-white/90 text-rani opacity-0 group-hover:opacity-100"
          }`}
          data-testid={`wishlist-${product.id}`}
          aria-label="Toggle wishlist"
        >
          <Heart className={`h-4 w-4 ${wished ? "fill-white" : ""}`} />
        </button>
      </div>
      <div className="p-5">
        <p className="text-xs uppercase tracking-[0.2em] text-marigold font-bold">{product.category.replace("-", " ")}</p>
        <h3 className="font-display text-lg text-deepbrown mt-1 leading-tight line-clamp-1">{product.name}</h3>
        <div className="mt-3 flex items-end justify-between">
          <p className="font-display text-2xl font-bold text-rani">₹{product.price.toLocaleString("en-IN")}</p>
          {product.stock > 5 ? (
            <span className="text-[10px] uppercase tracking-widest font-bold text-emerald" data-testid={`stock-${product.id}`}>In stock</span>
          ) : product.stock > 0 ? (
            <span className="text-[10px] uppercase tracking-widest font-bold text-marigold" data-testid={`stock-${product.id}`}>Only {product.stock} left</span>
          ) : (
            <span className="text-[10px] uppercase tracking-widest font-bold text-deepbrown/40" data-testid={`stock-${product.id}`}>Sold out</span>
          )}
        </div>
        <p className="mt-2 text-xs text-deepbrown/60" data-testid={`stock-count-${product.id}`}>
          {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
        </p>
      </div>
    </Link>
  );
}
